import { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Copy, Check, Eye, Heart, Share2, ShoppingCart, ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "sonner";
import Header from "../components/Header";
import Footer from "../components/Footer";
import MasonryGrid from "../components/MasonryGrid";
import { photoDetail, searchPhotos } from "../lib/api";
import { getPricingConfig, getQuote } from "../lib/commerce";
import { megapixels, tagsFromAlt } from "../lib/exif";
import { useAuth } from "../lib/auth";
import { useCart } from "../lib/cart";

// Deterministic pixel dimensions per size, derived from photo width/height.
function sizePixels(sizeKey, longSide, w, h) {
  if (!w || !h) return "—";
  if (sizeKey === "original" || longSide == null) return `${w} × ${h} px`;
  const long = Math.max(w, h);
  const ratio = Math.min(1, longSide / long);
  const nw = Math.max(1, Math.round(w * ratio));
  const nh = Math.max(1, Math.round(h * ratio));
  return `${nw} × ${nh} px`;
}

export default function PhotoDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { requireAuth, isFavorited, toggleFavorite } = useAuth();
  const { add: addCartItem } = useCart();

  const [photo, setPhoto] = useState(null);
  const [error, setError] = useState("");
  const [cfg, setCfg] = useState(null);
  const [sel, setSel] = useState({ size: "medium", license: "commercial", use: "individual", pack: "single" });
  const [quote, setQuote] = useState(null);
  const [similar, setSimilar] = useState([]);
  const [copied, setCopied] = useState(false);
  const [adding, setAdding] = useState(false);
  const [sizeOpen, setSizeOpen] = useState(false);

  useEffect(() => {
    setPhoto(null); setError(""); setSimilar([]);
    photoDetail(id).then((p) => {
      setPhoto(p);
      const kws = tagsFromAlt(p.alt, 3);
      const q = kws.length ? kws.slice(0, 2).join(" ") : "photography";
      searchPhotos({ query: q, per_page: 12, page: 1 })
        .then((d) => setSimilar((d.photos || []).filter((x) => String(x.id) !== String(id)).slice(0, 10)))
        .catch(() => {});
    }).catch((e) => setError(e?.response?.data?.detail?.error || "Failed to load photo"));
  }, [id]);

  useEffect(() => { getPricingConfig().then(setCfg).catch(() => {}); }, []);
  useEffect(() => { getQuote(sel).then(setQuote).catch(() => {}); }, [sel]);

  const faved = photo ? isFavorited(photo.id) : false;
  const dims = useMemo(() => (photo ? `${photo.width} × ${photo.height}` : "—"), [photo]);

  const copyId = useCallback(() => {
    if (!photo) return;
    try {
      const p = navigator.clipboard?.writeText?.(String(photo.id));
      if (p && typeof p.then === "function") p.catch(() => {});
    } catch { /* noop */ }
    setCopied(true); setTimeout(() => setCopied(false), 1500);
  }, [photo]);

  const doPreview = () => {
    const url = photo?.src?.large || photo?.src?.original || photo?.src?.medium;
    if (url) window.open(url, "_blank", "noopener,noreferrer");
  };

  const doShare = async () => {
    if (!photo) return;
    const shareData = { title: photo.alt || "EliteStock", text: photo.alt || "", url: window.location.href };
    try {
      if (navigator.share) { await navigator.share(shareData); return; }
    } catch { /* user cancelled */ }
    try {
      await navigator.clipboard?.writeText?.(window.location.href);
      toast.success("Link copied to clipboard");
    } catch { toast.error("Unable to share"); }
  };

  const doAddToCart = () => {
    if (!photo) return;
    requireAuth(async () => {
      setAdding(true);
      try {
        await addCartItem({ photo, ...sel });
        toast.success("Added to cart", { description: `${sel.size} · ${sel.license} · ${labelForPack(sel.pack)}` });
      } catch { toast.error("Failed to add to cart"); }
      finally { setAdding(false); }
    });
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="pt-16">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 pt-4 pb-8">
          {error && <div data-testid="detail-error" className="p-4 border border-destructive/30 bg-destructive/5 text-destructive text-sm">{error}</div>}
          {!photo && !error && <div className="py-24 text-center text-sm text-muted-foreground">Loading…</div>}

          {photo && (
            <>
              {/* Full-width headline row ABOVE the 70/30 grid */}
              <div className="mb-4">
                <h1 data-testid="detail-title" className="font-serif text-2xl md:text-3xl text-ink leading-tight">{photo.alt || "Untitled"}</h1>
                <div className="mt-1 text-sm text-muted-foreground">
                  by <a href={photo.photographer?.url || "#"} target="_blank" rel="noreferrer" data-testid="detail-photographer" className="text-ink hover:text-gold es-gold-underline">{photo.photographer?.name || "Unknown"}</a>
                  <span className="mx-2 text-muted-foreground/50">·</span>
                  <span>{megapixels(photo.width, photo.height)}</span>
                  <span className="mx-2 text-muted-foreground/50">·</span>
                  <span data-testid="detail-dims">{dims}</span>
                </div>
              </div>

            <div className="grid grid-cols-1 lg:grid-cols-[7fr_3fr] gap-10 items-start">
              {/* ============ LEFT (70%) ============ */}
              <div className="min-w-0">
                {/* Large preview */}
                <div className="bg-white border border-border flex items-center justify-center"
                  style={{ backgroundColor: photo.avg_color || "#e9e5dd", height: "62vh", minHeight: "360px" }}>
                  <img data-testid="detail-image" src={photo.src?.large || photo.src?.medium} alt={photo.alt || ""} className="max-h-full max-w-full object-contain es-fade-in" />
                </div>

                {/* Compact action bar */}
                <div className="mt-3 flex flex-wrap items-center gap-x-6 gap-y-2 border-t border-border pt-3 text-sm">
                  <div className="flex items-center gap-2">
                    <span className="text-[11px] uppercase tracking-[0.25em] text-muted-foreground">Asset ID</span>
                    <button onClick={copyId} data-testid="asset-id-copy" className="inline-flex items-center gap-1 font-mono text-ink hover:text-gold">
                      {photo.id} {copied ? <Check className="h-3.5 w-3.5 text-gold" /> : <Copy className="h-3.5 w-3.5" />}
                    </button>
                  </div>
                  <div className="ml-auto flex items-center gap-x-6 gap-y-2 flex-wrap">
                    <button data-testid="action-preview" onClick={doPreview} className="inline-flex items-center gap-1.5 text-ink hover:text-gold">
                      <Eye className="h-4 w-4" /> Preview
                    </button>
                    <button data-testid="action-save" onClick={() => toggleFavorite(photo)} className={`inline-flex items-center gap-1.5 ${faved ? "text-gold" : "text-ink hover:text-gold"}`}>
                      <Heart className={`h-4 w-4 ${faved ? "fill-current" : ""}`} /> {faved ? "Saved" : "Save"}
                    </button>
                    <button data-testid="action-share" onClick={doShare} className="inline-flex items-center gap-1.5 text-ink hover:text-gold">
                      <Share2 className="h-4 w-4" /> Share
                    </button>
                  </div>
                </div>
              </div>

              {/* ============ RIGHT RAIL (30%) ============ */}
              <aside className="lg:sticky lg:top-24 space-y-5" data-testid="pricing-configurator">
                {/* STEP 1 */}
                <div className="border border-border bg-white">
                  <StepHeader n={1} label="Choose license" />
                  <div className="p-4 space-y-4">
                    {/* Individual / Team — teal banner */}
                    {cfg && (
                      <div className="grid grid-cols-2 border border-border overflow-hidden">
                        {cfg.uses.map((o) => {
                          const active = sel.use === o.key;
                          return (
                            <button key={o.key} onClick={() => setSel((s) => ({ ...s, use: o.key }))}
                              data-testid={`config-use-${o.key}`}
                              className={`px-3 py-2.5 text-sm text-center transition-colors ${active ? "bg-gold text-ivory" : "bg-white text-ink hover:bg-muted/40"}`}>
                              <div className="font-medium">{o.label}</div>
                              <div className={`text-[10px] mt-0.5 ${active ? "text-ivory/85" : "text-muted-foreground"}`}>{o.desc}</div>
                            </button>
                          );
                        })}
                      </div>
                    )}

                    {/* Editorial / Commercial */}
                    {cfg && (
                      <div>
                        <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground mb-1.5">Usage</div>
                        <div className="grid grid-cols-2 gap-2">
                          {cfg.licenses.map((o) => {
                            const active = sel.license === o.key;
                            return (
                              <button key={o.key} onClick={() => setSel((s) => ({ ...s, license: o.key }))}
                                data-testid={`config-license-${o.key}`}
                                className={`text-left px-3 py-2 border transition-colors ${active ? "border-gold bg-gold text-ivory" : "border-border bg-white text-ink hover:border-ink"}`}>
                                <div className="text-sm font-medium">{o.label}</div>
                                <div className={`text-[10px] mt-0.5 ${active ? "text-ivory/85" : "text-muted-foreground"}`}>{o.desc}</div>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Expandable Size / Resolution */}
                    {cfg && (
                      <div className="border border-border">
                        <button onClick={() => setSizeOpen((v) => !v)}
                          data-testid="size-expander-toggle"
                          className="w-full flex items-center justify-between px-3 py-2.5 text-left hover:bg-muted/30">
                          <div>
                            <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground">Resolution</div>
                            <div className="text-sm text-ink">
                              {labelForSize(cfg, sel.size)} · <span className="text-muted-foreground">{sizePixels(sel.size, longSideFor(cfg, sel.size), photo.width, photo.height)}</span>
                            </div>
                          </div>
                          {sizeOpen ? <ChevronUp className="h-4 w-4 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
                        </button>
                        {sizeOpen && (
                          <div className="border-t border-border divide-y divide-border">
                            {cfg.sizes.map((o) => {
                              const active = sel.size === o.key;
                              const px = sizePixels(o.key, o.long_side, photo.width, photo.height);
                              return (
                                <button key={o.key} onClick={() => { setSel((s) => ({ ...s, size: o.key })); setSizeOpen(false); }}
                                  data-testid={`config-size-${o.key}`}
                                  className={`w-full flex items-center justify-between px-3 py-2.5 text-left transition-colors ${active ? "bg-muted/40" : "hover:bg-muted/20"}`}>
                                  <div>
                                    <div className="text-sm text-ink flex items-center gap-2">
                                      {o.label}
                                      {active && <span className="inline-flex items-center gap-1 text-[10px] text-gold"><Check className="h-3 w-3" /> selected</span>}
                                    </div>
                                    <div className="text-[11px] text-muted-foreground">{px}</div>
                                  </div>
                                  <div className="text-xs text-muted-foreground">${o.base} base</div>
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* STEP 2 */}
                <div className="border border-border bg-white">
                  <StepHeader n={2} label="Choose pack" />
                  <div className="p-4 space-y-2">
                    {cfg && quote && cfg.packs.map((o) => {
                      const active = sel.pack === o.key;
                      // Compute preview for this pack against current size/license/use.
                      const unit = quote.unit_price;
                      const sub = unit * o.count;
                      const total = Math.round(sub * (1 - (o.discount || 0)));
                      const eff = Math.round(total / o.count);
                      const savings = sub - total;
                      const pct = Math.round((o.discount || 0) * 100);
                      return (
                        <button key={o.key} onClick={() => setSel((s) => ({ ...s, pack: o.key }))}
                          data-testid={`config-pack-${o.key}`}
                          className={`w-full text-left px-3 py-3 border transition-colors ${active ? "border-gold bg-gold/5" : "border-border bg-white hover:border-ink"}`}>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${active ? "border-gold" : "border-border"}`}>
                                {active && <div className="w-2 h-2 rounded-full bg-gold" />}
                              </div>
                              <div className="text-sm font-medium text-ink">{o.label}</div>
                              {pct > 0 && (
                                <span className={`text-[10px] px-1.5 py-0.5 rounded-sm ${active ? "bg-gold text-ivory" : "bg-ink text-ivory"}`}>-{pct}%</span>
                              )}
                            </div>
                            <div className="text-right">
                              <div className="font-serif text-lg text-ink leading-none">${total}</div>
                              {savings > 0 && <div className="text-[10px] text-gold mt-0.5">save ${savings}</div>}
                            </div>
                          </div>
                          <div className="mt-1 pl-6 flex items-center justify-between text-[11px] text-muted-foreground">
                            <span>${eff} / image × {o.count}</span>
                            {savings > 0 && <span className="line-through">${sub}</span>}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Total + CTA */}
                {quote && (
                  <div className="border border-ink bg-white p-4 space-y-3" data-testid="quote-total">
                    <div className="flex items-baseline justify-between">
                      <div className="text-[11px] uppercase tracking-[0.3em] text-muted-foreground">Total</div>
                      <div className="font-serif text-3xl text-ink">${quote.total}</div>
                    </div>
                    <div className="text-xs text-muted-foreground -mt-1 flex items-center justify-between">
                      <span>${quote.effective_unit} / image × {quote.pack_count}</span>
                      {quote.savings > 0 && <span className="text-gold">You save ${quote.savings}</span>}
                    </div>
                    <button data-testid="cart-add-btn" onClick={doAddToCart} disabled={adding}
                      className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 bg-gold text-ivory hover:bg-gold/90 disabled:opacity-60 text-sm tracking-wide">
                      <ShoppingCart className="h-4 w-4" /> {adding ? "Adding…" : "Add to cart"}
                    </button>
                  </div>
                )}
              </aside>
            </div>
            </>
          )}

          {/* Similar */}
          {similar.length > 0 && (
            <section className="mt-16" data-testid="similar-section">
              <div className="text-[11px] uppercase tracking-[0.3em] text-muted-foreground">Similar stock images</div>
              <h2 className="font-serif text-2xl md:text-3xl text-ink mt-1 mb-6">You might also like</h2>
              <MasonryGrid photos={similar} onOpen={(p) => nav(`/dashboard/photo/${p.id}`)} loading={false} hasMore={false} testId="similar-masonry" />
            </section>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
}

function StepHeader({ n, label }) {
  return (
    <div className="flex items-center gap-3 px-4 py-3 bg-ink text-ivory">
      <div className="w-6 h-6 rounded-full bg-gold text-ivory text-xs flex items-center justify-center font-medium">{n}</div>
      <div className="text-[11px] uppercase tracking-[0.3em]">{label}</div>
    </div>
  );
}

function labelForSize(cfg, key) {
  return cfg?.sizes?.find((s) => s.key === key)?.label || key;
}
function longSideFor(cfg, key) {
  return cfg?.sizes?.find((s) => s.key === key)?.long_side ?? null;
}
function labelForPack(key) {
  return { single: "Single", "5pack": "5-Pack", "10pack": "10-Pack", "25pack": "25-Pack" }[key] || key;
}
